import { useRef, useState, useEffect } from 'react';
import { Link as NextLink, TabLink as NextNavLink } from '@framework/router/Next/Link';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import HeaderBanner from '@framework/ui/Organisms/HeaderBanner';
import isDefined from '@framework/data-helpers/isDefined';
import useElementSize from '@framework/hooks/useElementSize';
import { Tenant } from '@framework/tenant/vo/types/Tenant';
import dealroadshowConfig from '@drs/config';
import evercallConfig from '@evercall/config';

import hInteraction from '@dealroadshow/uikit-styles/helpers/interaction.module.scss';
import styles from './header.module.scss';
import IconLock from '@dealroadshow/uikit-icon/IconLock';
import IconClose from '@dealroadshow/uikit-icon/IconClose';
import IconHamburgerMenu from '@dealroadshow/uikit-icon/IconHamburgerMenu';

interface IProps {
  tenant: Tenant,
  logo: string,
  menu?: {
    key: string,
    title: string,
    path: string,
  }[],
  button?: () => React.ReactNode,
  loginButton?: () => React.ReactNode,
  loginUrl?: string,
  registerUrl?: string,
  animatedHeader?: boolean,
  isWideMenu?: boolean,
}

const Header = ({
  menu: propMenu = [],
  // @ts-ignore
  button = null,
  isWideMenu = false,
  ...otherProps
}: IProps) => {
  const props = {
    menu: propMenu,
    button,
    ...otherProps,
  };

  const headerRef = useRef(null);
  const lastScrollPos = useRef(0);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const { height: headerHeight } = useElementSize('landingHeader');

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isMobileMenuOpened, setMobileMenuOpened] = useState(false);

  const isDealroadshow = props.tenant === dealroadshowConfig.code;
  const isEvercall = props.tenant === evercallConfig.code;

  useEffect(() => {
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Hide or show the menu.
  const handleScroll = throttle(() => {
    setIsHeaderFixed(window.scrollY > 0);
    // Fix for throttled scroll on redirect
    if (!headerRef.current) {
      return;
    }

    const currentScroll = document.documentElement.scrollTop;
    // @ts-ignore
    const isSticky = currentScroll > headerRef.current.clientHeight;

    if (isSticky) {
      if (currentScroll > 0 && lastScrollPos.current <= currentScroll) {
        lastScrollPos.current = currentScroll;
        setIsHeaderVisible(false);
      } else {
        lastScrollPos.current = currentScroll;
        setIsHeaderVisible(true);
      }
    } else {
      setIsHeaderVisible(true);
    }
  }, 100);

  const onResize = throttle(() => {
    handleMobileMenuToggle(false);
  }, 100);

  const handleMobileMenuToggle = (toggle?: boolean) => {
    toggle = isDefined(toggle) ? toggle : !isMobileMenuOpened;
    // @ts-ignore
    setMobileMenuOpened(toggle);
    // IE11 Fix
    if (toggle) {
      document.body.classList.add(hInteraction.nonScrollable);
    } else {
      document.body.classList.remove(hInteraction.nonScrollable);
    }
  };

  const handleMobileMenuLink = () => {
    if (isMobileMenuOpened) {
      handleMobileMenuToggle(false);
    }
  };

  const renderLoginButton = () => {
    const {
      loginButton,
      loginUrl,
    } = props;

    if (loginButton) {
      return loginButton();
    }

    if (loginUrl) {
      return (
        <a
          suppressHydrationWarning
          href={ loginUrl }
          className={ cn(styles.menuLink, styles.menuIconLink, styles.menuLoginButton) }
          data-test="headerLogIn"
        >
          <IconLock
            className={ styles.menuLoginIcon }
          />
          Log In
        </a>
      );
    }

    return null;
  };

  const renderRegisterButton = () => (props.registerUrl ? (
    <a
      suppressHydrationWarning
      href={ props.registerUrl }
      className={ styles.menuLink }
      data-test="headerRegister"
    >
      Register
    </a>
  ) : null);

  const renderHeaderButton = () => {
    const { button } = props;

    return button ? (
      <span
        className={ styles.headerBtn }
        onClick={ () => handleMobileMenuToggle(false) }
      >
        { button && button() }
      </span>
    ) : null;
  };

  const renderHeaderActions = (): React.ReactElement => (
    <>
      { !isEvercall && renderLoginButton() }
      { !isEvercall && !isDealroadshow && renderRegisterButton() }
      { renderHeaderButton() }
    </>
  );

  const renderMobileHeaderActions = (): React.ReactElement => (
    <>
      { !isEvercall && !isDealroadshow && renderRegisterButton() }
      { renderHeaderButton() }
    </>
  );

  const renderMenu = (menu: { [key: string]: any }[]): React.ReactElement[] => menu.map((menuItem) => (
    <NextNavLink
      key={ menuItem.key }
      to={ menuItem.path }
      title={ menuItem.title }
      className={ styles.menuLink }
      activeClassName={ styles.isMenuLinkActive }
      isExactActive
      { ...(menuItem?.isActive && { isActive: menuItem.isActive() }) }
      onClick={ handleMobileMenuLink }
      dataTest={ `${ menuItem.key }NavLink` }
    >
      { menuItem.title }
    </NextNavLink>
  ));

  const headerCls = cn(`tenant-${ props.tenant }`, {
    [styles.animatedHeader]: props.animatedHeader,
    [styles.isHeaderHidden]: !isHeaderVisible,
  });

  const menuCls = cn(styles.menu, {
    [styles.isMobileMenu]: isMobileMenuOpened,
  });

  const { menu } = props;
  const sortedMenu = [...menu].sort((prev, next) => (prev.title > next.title ? 1 : -1));

  const hasMenu = props.menu.length > 0;
  const hasMobileActions = !!props.registerUrl || !!props.button;
  const hasBurger = hasMenu || hasMobileActions;

  return (
    <>
      { isMobileMenuOpened && (
        <div
          className={ styles.headerOverlay }
          onClick={ () => handleMobileMenuToggle(false) }
        />
      ) }
      { isHeaderFixed && <div style={ { height: headerHeight } } /> }
      <div
        className={ cn(styles.header, {
          [styles.headerFixed]: isHeaderFixed,
        }) }
        id="landingHeader"
      >
        <HeaderBanner isSticky={ false } />
        <div className={ headerCls } ref={ headerRef }>
          <div className={ styles.headerWrp }>
            <div className={ cn(styles.headerContainer, { [styles.wideMenuContainer]: isWideMenu }) }>
              <div>
                { hasBurger && (
                  <div
                    className={ cn(styles.burger, {
                      [styles.tenantDealroadshow]: props.tenant === dealroadshowConfig.code,
                    }) }
                    onClick={ () => handleMobileMenuToggle() }
                  >
                    { !isMobileMenuOpened ? <IconHamburgerMenu /> : <IconClose /> }
                  </div>
                ) }
                <div className={ styles.logo }>
                  <NextLink
                    to="/"
                    onClick={ () => handleMobileMenuToggle(false) }
                  >
                    <img
                      alt="Logo"
                      src={ props.logo }
                    />
                  </NextLink>
                </div>
                <div className={ menuCls }>
                  <div className={ styles.headerMenu }>
                    { renderMenu(menu) }
                  </div>
                  <div className={ styles.mobileHeaderMenu }>
                    { renderMenu(sortedMenu) }
                  </div>
                  <div className={ styles.mobileHeaderUserActions }>
                    { renderMobileHeaderActions() }
                  </div>
                </div>
              </div>
              <div className={ styles.headerActions }>
                <div className={ styles.headerUserActions } data-test="headerUserActions">
                  { renderHeaderActions() }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
